import api from './api';

const jornadaService = {
  /**
   * Obtiene la próxima jornada sin disputar
   * @param {number} sedeId - ID de la sede (opcional)
   * @returns {Promise} Datos de la próxima jornada
   */
  async getProximaJornada(sedeId = null) {
    try {
      const params = sedeId ? { sedeId } : {};
      const response = await api.get('/postgres/jornadas/proxima', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener próxima jornada:', error);
      throw error;
    }
  },

  /**
   * Obtiene las próximas jornadas
   * @param {number} limit - Número de jornadas a obtener
   * @param {number} sedeId - ID de la sede (opcional)
   * @returns {Promise} Lista de próximas jornadas
   */
  async getProximasJornadas(limit = 5, sedeId = null) {
    try {
      const params = { limit };
      if (sedeId) params.sede_id = sedeId;
      const response = await api.get('/postgres/jornadas/proximas', { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener próximas jornadas:', error);
      throw error;
    }
  },

  /**
   * Obtiene todas las jornadas con filtros
   * @param {Object} filters - Filtros opcionales
   * @returns {Promise} Lista de jornadas
   */
  async getJornadas(filters = {}) {
    try {
      const response = await api.get('/postgres/jornadas', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error al obtener jornadas:', error);
      throw error;
    }
  },

  /**
   * Obtiene todos los futbolistas registrados para exportación
   * @returns {Promise} Lista de futbolistas con información completa
   */
  async getAllFutbolistasForExport() {
    try {
      const response = await api.get('/postgres/jornadas/export/futbolistas');
      return response.data;
    } catch (error) {
      console.error('Error al obtener futbolistas para exportación:', error);
      throw error;
    }
  },

  /**
   * Obtiene futbolistas inscritos por jornada para exportación
   * @param {number} torneoId - ID del torneo
   * @returns {Promise} Lista de futbolistas inscritos por jornada con información completa
   */
  async getFutbolistasInscritosPorJornadaForExport(torneoId) {
    try {
      const response = await api.get(`/postgres/jornadas/export/futbolistas-inscritos/${torneoId}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener futbolistas inscritos por jornada:', error);
      throw error;
    }
  },

  /**
   * Obtiene futbolistas inscritos de una jornada específica para exportación
   * @param {number} jornadaId - ID de la jornada
   * @returns {Promise} Lista de futbolistas inscritos en la jornada específica
   */
  async getFutbolistasInscritosJornadaEspecifica(jornadaId) {
    try {
      const response = await api.get(`/postgres/jornadas/${jornadaId}/export/futbolistas-inscritos`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener futbolistas inscritos de jornada específica:', error);
      throw error;
    }
  },

  /**
   * Obtiene futbolistas debutantes de una jornada específica para exportación
   * Debutantes = futbolistas que no han participado en jornadas anteriores
   * @param {number} jornadaId - ID de la jornada
   * @returns {Promise} Lista de futbolistas debutantes en la jornada específica con talla de equipación
   */
  async getFutbolistasDebutantesJornadaEspecifica(jornadaId) {
    try {
      const response = await api.get(`/postgres/jornadas/${jornadaId}/export/futbolistas-debutantes`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener futbolistas debutantes de jornada específica:', error);
      throw error;
    }
  },

  /**
   * Obtiene una jornada por ID
   * @param {number} id - ID de la jornada
   * @returns {Promise} Datos de la jornada
   */
  async getJornadaById(id) {
    try {
      const response = await api.get(`/postgres/jornadas/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener jornada:', error);
      throw error;
    }
  },

  /**
   * Obtiene futbolistas NO inscritos en una jornada específica
   * @param {number} jornadaId - ID de la jornada
   * @returns {Promise} Lista de futbolistas no inscritos con información del tutor
   */
  async getFutbolistasNoInscritos(jornadaId) {
    try {
      const response = await api.get(`/postgres/jornadas/${jornadaId}/no-inscritos`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener futbolistas no inscritos:', error);
      throw error;
    }
  },

  /**
   * Obtiene partidos organizados para una jornada específica
   * @param {number} jornadaId - ID de la jornada
   * @param {number} sedeId - ID de la sede
   * @returns {Promise} Lista de partidos organizados con equipos y futbolistas
   */
  async getPartidosOrganizados(jornadaId, sedeId) {
    try {
      const response = await api.get(`/postgres/jornadas/${jornadaId}/partidos-organizados`, {
        params: { sede_id: sedeId }
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener partidos organizados:', error);
      throw error;
    }
  },

  async organizarProximaJornada() {
    try {
      const response = await api.post('/postgres/jornadas/organizar-proxima');
      return response.data;
    } catch (error) {
      console.error('Error al organizar próxima jornada:', error);
      throw error;
    }
  },

  async simularProximaJornada() {
    try {
      const response = await api.post('/postgres/jornadas/simular-proxima');
      return response.data;
    } catch (error) {
      console.error('Error al simular próxima jornada:', error);
      throw error;
    }
  },

  async limpiarPartidosOrganizados(jornadaId, sedeId) {
    try {
      const response = await api.delete(`/postgres/jornadas/${jornadaId}/limpiar-partidos`, {
        params: { sede_id: sedeId }
      });
      return response.data;
    } catch (error) {
      console.error('Error al limpiar partidos organizados:', error);
      throw error;
    }
  },

  // Obtener futbolistas no asignados
  async getFutbolistasNoAsignados(jornadaId, sedeId) {
    try {
      const response = await api.get(`/postgres/jornadas/${jornadaId}/futbolistas-no-asignados`, {
        params: { sede_id: sedeId }
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener futbolistas no asignados:', error);
      throw error;
    }
  },

  async getFutbolistasDisponiblesManual(jornadaId, sedeId) {
    try {
      const response = await api.get(`/postgres/jornadas/${jornadaId}/futbolistas-disponibles-manual`, {
        params: { sede_id: sedeId }
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener futbolistas disponibles para asignación manual:', error);
      throw error;
    }
  },

  // Reasignar un futbolista no asignado
  async reasignarFutbolistaNoAsignado(jornadaId, futbolistaId, data) {
    try {
      const response = await api.post(`/postgres/jornadas/${jornadaId}/futbolistas/${futbolistaId}/reasignar`, data);
      return response.data;
    } catch (error) {
      console.error('Error al reasignar futbolista:', error);
      throw error;
    }
  },

  // Crear partido manual
  async crearPartidoManual(jornadaId, data) {
    try {
      const response = await api.post(`/postgres/jornadas/${jornadaId}/crear-partido-manual`, data);
      return response.data;
    } catch (error) {
      console.error('Error al crear partido manual:', error);
      throw error;
    }
  },

  // Asignar jugador a equipo
  async asignarJugadorAEquipo(jornadaId, futbolistaId, data) {
    try {
      const response = await api.post(`/postgres/jornadas/${jornadaId}/futbolistas/${futbolistaId}/asignar-equipo`, data);
      return response.data;
    } catch (error) {
      console.error('Error al asignar jugador a equipo:', error);
      throw error;
    }
  },

  // Eliminar partido completo
  async eliminarPartidoCompleto(partidoId) {
    try {
      const response = await api.delete(`/postgres/jornadas/partidos/${partidoId}/eliminar-completo`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar partido completo:', error);
      throw error;
    }
  },

  // Cerrar partido
  async cerrarPartido(partidoId) {
    try {
      const response = await api.put(`/postgres/jornadas/partidos/${partidoId}/cerrar`);
      return response.data;
    } catch (error) {
      console.error('Error al cerrar partido:', error);
      throw error;
    }
  },

  /**
   * Edita una jornada (fecha, hora, sede)
   * @param {number} jornadaId - ID de la jornada
   * @param {Object} datosEdicion - Datos a editar { fecha?, hora?, sede_en_torneo_id? }
   * @returns {Promise} Resultado de la edición
   */
  async editarJornada(jornadaId, datosEdicion) {
    try {
      const response = await api.put(`/postgres/jornadas/${jornadaId}/editar`, datosEdicion);
      return response.data;
    } catch (error) {
      console.error('Error al editar jornada:', error);
      throw error;
    }
  },

  /**
   * Confirma una jornada: marca como disputada y reasigna futbolistas que no jugaron
   * @param {number} jornadaId - ID de la jornada
   * @returns {Promise} Resultado de la confirmación
   */
  async confirmarJornada(jornadaId) {
    try {
      const response = await api.post(`/postgres/jornadas/${jornadaId}/confirmar`);
      return response.data;
    } catch (error) {
      console.error('Error al confirmar jornada:', error);
      throw error;
    }
  },

  async proponerPartidos(data) {
    try {
      const response = await api.post('/postgres/jornadas/proponer-partidos', data);
      return response.data;
    } catch (error) {
      console.error('Error al proponer partidos:', error);
      throw error;
    }
  },

  async getPropuestaPartidos(jornadaId, torneoId) {
    try {
      const response = await api.get(`/postgres/jornadas/${jornadaId}/propuesta-partidos`, {
        params: { torneoId }
      });
      return response.data;
    } catch (error) {
      console.error('Error al obtener propuesta de partidos:', error);
      throw error;
    }
  },

  async updateBorrador(borradorId, data) {
    try {
      const response = await api.put(`/postgres/jornadas/borradores/${borradorId}`, data);
      return response.data;
    } catch (error) {
      console.error('Error al actualizar borrador:', error);
      throw error;
    }
  },

  async moverJugadorEntreBorradores(data) {
    try {
      const response = await api.post('/postgres/jornadas/borradores/mover-jugador', data);
      return response.data;
    } catch (error) {
      console.error('Error al mover jugador entre borradores:', error);
      throw error;
    }
  },

  async confirmarBorradores(jornadaId, torneoId) {
    try {
      const response = await api.post(`/postgres/jornadas/${jornadaId}/confirmar-borradores`, { torneoId });
      return response.data;
    } catch (error) {
      console.error('Error al confirmar borradores:', error);
      throw error;
    }
  },

  async confirmarBorrador(borradorId, forzar = false) {
    try {
      const response = await api.post(`/postgres/jornadas/borradores/${borradorId}/confirmar`, { forzar });
      return response.data;
    } catch (error) {
      console.error('Error al confirmar borrador:', error);
      throw error;
    }
  },

  async revertirPartidoABorrador(partidoId) {
    try {
      const response = await api.post(`/postgres/jornadas/partidos/${partidoId}/revertir-a-borrador`);
      return response.data;
    } catch (error) {
      console.error('Error al revertir partido a borrador:', error);
      throw error;
    }
  },

  async crearBorradorVacio(jornadaId, torneoId, categoriaNombre, modalidad) {
    try {
      const response = await api.post(`/postgres/jornadas/${jornadaId}/crear-borrador`, {
        torneoId,
        categoriaNombre: categoriaNombre || 'Partido Manual',
        modalidad: modalidad || 'F8'
      });
      return response.data;
    } catch (error) {
      console.error('Error al crear borrador:', error);
      throw error;
    }
  },

  async getFutbolistasParaBorrador(borradorId, search = '') {
    try {
      const response = await api.get(`/postgres/jornadas/borradores/${borradorId}/futbolistas-disponibles`, {
        params: { search }
      });
      return response.data;
    } catch (error) {
      console.error('Error al buscar futbolistas para borrador:', error);
      throw error;
    }
  },

  async añadirJugadorABorrador(borradorId, data) {
    try {
      const response = await api.post(`/postgres/jornadas/borradores/${borradorId}/add-jugador`, data);
      return response.data;
    } catch (error) {
      console.error('Error al añadir jugador al borrador:', error);
      throw error;
    }
  },

  async getFutbolistasParaPartidoConfirmado(partidoId, search = '') {
    try {
      const response = await api.get(`/postgres/jornadas/partidos/${partidoId}/futbolistas-disponibles`, {
        params: { search }
      });
      return response.data;
    } catch (error) {
      console.error('Error al buscar futbolistas para partido confirmado:', error);
      throw error;
    }
  },

  async añadirJugadorAPartidoConfirmado(partidoId, data) {
    try {
      const response = await api.post(`/postgres/jornadas/partidos/${partidoId}/add-jugador`, data);
      return response.data;
    } catch (error) {
      console.error('Error al añadir jugador al partido confirmado:', error);
      throw error;
    }
  },

  async eliminarBorrador(borradorId) {
    try {
      const response = await api.delete(`/postgres/jornadas/borradores/${borradorId}`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar borrador:', error);
      throw error;
    }
  },

  // ===== ANOTACIONES DE PARTIDO =====

  async crearAnotacion(partidoId, { equipo_id, tipo, contenido }) {
    try {
      const response = await api.post(`/postgres/jornadas/partidos/${partidoId}/anotaciones`, {
        equipo_id, tipo, contenido
      });
      return response.data;
    } catch (error) {
      console.error('Error al crear anotación:', error);
      throw error;
    }
  },

  async getAnotaciones(partidoId) {
    try {
      const response = await api.get(`/postgres/jornadas/partidos/${partidoId}/anotaciones`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener anotaciones:', error);
      throw error;
    }
  },

  async eliminarAnotacion(anotacionId) {
    try {
      const response = await api.delete(`/postgres/jornadas/anotaciones/${anotacionId}`);
      return response.data;
    } catch (error) {
      console.error('Error al eliminar anotación:', error);
      throw error;
    }
  }
};

export default jornadaService;

